import { useEffect, useRef, useState } from "react";

import { getUserTier } from "../api/client";

/**
 * usePollUserTier — Pattern C polling hook (v1.0 Task 6.12 / Req 20.5b–5d).
 *
 * After the user completes Stripe Checkout, the client must detect when
 * the tier flips from "free" to "paid" in Cognito. The Stripe webhook
 * updates the custom:tier attribute server-side; ID tokens cached on the
 * client lag that update, so the spec uses an HTTP poll against
 * /user/tier (which reads the live attribute) to drive the UI transition.
 *
 * Behavior:
 *   - Inactive (active=false): does nothing, returns tier=null.
 *   - When active flips true: polls every `intervalMs` (default 3000ms).
 *   - Stops polling automatically the first time the response is "paid".
 *   - Times out after `timeoutMs` (default 60s) — the Req 20.5e P95 latency
 *     target is 35s end-to-end, so 60s gives headroom before surfacing an
 *     error state to the user.
 *
 * Caller drives `active` based on the Stripe Checkout-return state and
 * uses `tier` (or `error`) to swap LockPanels for paid content / a retry CTA.
 */
export interface UsePollUserTierResult {
  /** Latest live tier value, or null before the first successful poll. */
  tier: "free" | "paid" | null;
  /** Polling-loop error (network / 5xx / timeout). */
  error: { code: string; message: string } | null;
  /** True while the poll loop is running. */
  polling: boolean;
}

interface UsePollUserTierOptions {
  /** Caller-controlled gate — typically true after Stripe Checkout returns. */
  active: boolean;
  /** Milliseconds between polls. Defaults to 3000 per Task 6.12 spec. */
  intervalMs?: number;
  /** Hard ceiling on the polling loop. */
  timeoutMs?: number;
}

export function usePollUserTier({
  active,
  intervalMs = 3000,
  timeoutMs = 60_000,
}: UsePollUserTierOptions): UsePollUserTierResult {
  const [tier, setTier] = useState<"free" | "paid" | null>(null);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  const [polling, setPolling] = useState(false);

  // useRef so stopping doesn't trigger a re-render storm; the cleanup
  // function uses the current refs without invalidating dependencies.
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!active) {
      setPolling(false);
      return;
    }

    cancelledRef.current = false;
    setPolling(true);
    setError(null);

    const startedAt = Date.now();

    async function pollOnce(): Promise<boolean> {
      try {
        const res = await getUserTier();
        if (cancelledRef.current) return true; // unmounted mid-flight
        setTier(res.tier);
        if (res.tier === "paid") {
          setPolling(false);
          return true;
        }
      } catch (e) {
        if (cancelledRef.current) return true;
        const err = e as { code?: string; message?: string };
        setError({
          code: err.code || "poll_failed",
          message: err.message || "Could not check tier.",
        });
        // Keep polling — a transient 5xx shouldn't kill the loop. The
        // timeout below is the hard ceiling.
      }
      return false;
    }

    // Fire immediately so the user doesn't sit on a loading spinner for
    // the first interval after Stripe redirects them back.
    void pollOnce();

    const handle = window.setInterval(async () => {
      if (Date.now() - startedAt > timeoutMs) {
        setError({
          code: "tier_poll_timeout",
          message:
            "Upgrade is taking longer than expected. Please refresh in a moment.",
        });
        setPolling(false);
        window.clearInterval(handle);
        return;
      }
      const done = await pollOnce();
      if (done) window.clearInterval(handle);
    }, intervalMs);

    return () => {
      cancelledRef.current = true;
      window.clearInterval(handle);
      setPolling(false);
    };
  }, [active, intervalMs, timeoutMs]);

  return { tier, error, polling };
}
